
import { zip as observableZip, Subscription } from 'rxjs';
import { Component } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { LoggedInUserService } from '../../auth/logged.in.user';
import { SearchComponent } from '../../components/common/search/search.component';
import { Configuration } from '../../configuration';
import { Helpers } from '../../helpers';
import { BookedEventSummaryDto } from '../../models/training/training.models';
import { TrainingSearchModel, TrainingSummaryDto } from '../../models/training/training.search.models';
import { TrainingService } from '../../services/training.service';

@Component({
    selector: 'training',
    template: require('./training.component.html'),
    styles: [require('./training.shared.css'), require('./training.component.css')]
})
export class TrainingComponent extends SearchComponent<TrainingSearchModel, TrainingSummaryDto> {

    bookedEvents: BookedEventSummaryDto[];
    routeParamsSub: Subscription;
    private routerEventsSub: Subscription;
    subscribed: boolean;
    viewMode: 'upcoming' | 'booked' = 'upcoming';

    constructor(private trainingService: TrainingService,
        configuration: Configuration,
        helpers: Helpers,
        private router: Router,
        private route: ActivatedRoute,
        loggedInUserService: LoggedInUserService) {
        super(new TrainingSearchModel(configuration), trainingService, helpers);

        var currentUser = loggedInUserService.getCurrentUser();

        if (currentUser) {
            this.request.customerId = currentUser.userId;
        } else {
            this.request.customerId = -1;
        }

        this.subscribed = !currentUser.canAdHoc('notSubscribed');

        this.request.sortProperty = 'eventDateTime';
        this.request.descending = false;
    }

    ngOnInit() {
        // Apply training-search-fit class to fit page height without scrollbar
        document.querySelector('body').classList.add('training-search-fit');

        this.route.queryParams.subscribe(params => {
            const tab = (params && (params as any).tab ? (params as any).tab : '').toString().toLowerCase();
            if (tab === 'booked') {
                this.router.navigate(['/training/booked'], { replaceUrl: true });
            }
            if (tab === 'upcoming') {
                this.router.navigate(['/training/upcoming'], { replaceUrl: true });
            }
        });

        this.setViewModeFromUrl();
        this.routerEventsSub = this.router.events.subscribe(evt => {
            if (evt && (evt as any) instanceof NavigationEnd) {
                const previousMode = this.viewMode;
                this.setViewModeFromUrl();
                if (previousMode !== this.viewMode && this.viewMode === 'booked') {
                    this.loadBookedEvents();
                }
            }
        });

        if (this.viewMode === 'booked') {
            this.loadBookedEvents();
        }

        super.ngOnInit();
    }

    ngOnDestroy() {
        // Remove training-search-fit class when leaving training page
        document.querySelector('body').classList.remove('training-search-fit');

        if (this.routerEventsSub) {
            this.routerEventsSub.unsubscribe();
        }
    }

    private setViewModeFromUrl() {
        const url = (this.router && this.router.url ? this.router.url : '').toLowerCase();
        this.viewMode = url.indexOf('/training/booked') > -1 ? 'booked' : 'upcoming';
    }

    private loadBookedEvents() {
        const eventObservable = this.trainingService.getBookedEvents();

        observableZip(
            eventObservable,
            (bookedEvents: BookedEventSummaryDto[]) => ({ bookedEvents }))
            .subscribe(results => {
                this.bookedEvents = results.bookedEvents;
            });
    }

    getTimeRange(startEnd: string): string {
        if (!startEnd) {
            return '';
        }
        const trimmed = startEnd.toString().trim();

        // Prefer explicit am/pm tokens if present, and infer missing suffix on end time.
        // Examples handled:
        // - "12:30pm\n4:45pm" => "12:30pm to 4:45pm"
        // - "12:30pm\n4:45"   => "12:30pm to 4:45pm"
        // - "12:30 pm - 4:45" => "12:30pm to 4:45pm"
        const explicit = [...trimmed.matchAll(/(\d{1,2}:\d{2})\s*(am|pm)\b/ig)]
            .map(m => ({ time: m[1], suffix: m[2].toLowerCase() }));
        if (explicit.length >= 2) {
            return this.toCompactAmPmWithSuffix(explicit[0].time, explicit[0].suffix) + ' to ' + this.toCompactAmPmWithSuffix(explicit[1].time, explicit[1].suffix);
        }
        if (explicit.length === 1) {
            const suffix = explicit[0].suffix;
            const allTimes = trimmed.match(/\b\d{1,2}:\d{2}\b/g) || [];
            if (allTimes.length >= 2) {
                return this.toCompactAmPmWithSuffix(allTimes[0], suffix) + ' to ' + this.toCompactAmPmWithSuffix(allTimes[1], suffix);
            }
        }

        const timeMatches = trimmed.match(/\b\d{1,2}:\d{2}\b/g);
        if (timeMatches && timeMatches.length >= 2) {
            const start = this.toCompactAmPm(timeMatches[0]);
            const end = this.toCompactAmPm(timeMatches[1]);
            return start + ' to ' + end;
        }
        const parts = trimmed.split(/\r?\n/).map(x => x.trim()).filter(x => x.length > 0);
        if (parts.length >= 2) {
            return parts[1];
        }
        // Fallback: sometimes it's "DATE - TIME" in a single line
        const dashSplit = trimmed.split(' - ');
        if (dashSplit.length >= 2) {
            return dashSplit.slice(1).join(' - ').trim();
        }
        return trimmed;
    }

    private toCompactAmPm(time24: string): string {
        if (!time24) {
            return '';
        }
        const match = time24.trim().match(/^(\d{1,2}):(\d{2})$/);
        if (!match) {
            return time24.trim();
        }
        const hour24 = parseInt(match[1], 10);
        const minutes = match[2];
        if (isNaN(hour24)) {
            return time24.trim();
        }
        const suffix = hour24 >= 12 ? 'pm' : 'am';
        const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
        return hour12.toString() + ':' + minutes + suffix;
    }

    private toCompactAmPmWithSuffix(time: string, suffix: string): string {
        if (!time) {
            return '';
        }
        const match = time.trim().match(/^(\d{1,2}):(\d{2})$/);
        if (!match) {
            return time.trim() + (suffix ? suffix.toLowerCase() : '');
        }
        const hour = parseInt(match[1], 10);
        const minutes = match[2];
        if (isNaN(hour)) {
            return time.trim() + (suffix ? suffix.toLowerCase() : '');
        }
        const normalizedSuffix = suffix && suffix.toLowerCase() === 'pm' ? 'pm' : 'am';
        const hour12 = hour % 12 === 0 ? 12 : hour % 12;
        return hour12.toString() + ':' + minutes + normalizedSuffix;
    }

    getDateFromStartEnd(startEnd: string): string {
        if (!startEnd) {
            return '';
        }
        const trimmed = startEnd.toString().trim();
        const parts = trimmed.split(/\r?\n/).map(x => x.trim()).filter(x => x.length > 0);
        if (parts.length >= 1) {
            // Some payloads include time on the same line; strip any HH:mm occurrences.
            return parts[0]
                .replace(/\b\d{1,2}:\d{2}\b/g, '')
                .replace(/\b(?:am|pm)\b/ig, '')
                .replace(/\b(?:am|pm)\s*-\s*(?:am|pm)\b/ig, '')
                .replace(/\s{2,}/g, ' ')
                .replace(/\s*-\s*$/g, '')
                .trim();
        }
        const dashSplit = trimmed.split(' - ');
        const datePart = dashSplit.length >= 1 ? dashSplit[0].trim() : trimmed;
        return datePart
            .replace(/\b\d{1,2}:\d{2}\b/g, '')
            .replace(/\b(?:am|pm)\b/ig, '')
            .replace(/\b(?:am|pm)\s*-\s*(?:am|pm)\b/ig, '')
            .replace(/\s{2,}/g, ' ')
            .replace(/\s*-\s*$/g, '')
            .trim();
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Client/app/views/training/training.component.ts